import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatDateITFull, formatDateIT, toISODate, WEEKDAYS_SHORT, MONTHS } from "@/lib/date";
import { toast } from "sonner";
import { Clock, Users, ChevronLeft, ChevronRight, Info, Check } from "lucide-react";

/**
 * 3-step client booking wizard: Service → Date (month calendar) → Time.
 * Reuses backend /api/bookings/available-slots for BOTH date and time availability
 * (single source of truth: no separate logic here).
 */
export default function ClientBookingView({ onChanged }) {
  const [services, setServices] = useState([]);
  const [serviceType, setServiceType] = useState("");
  const [monthCursor, setMonthCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [data, setData] = useState({ slots: [], duration_minutes: 0 });
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: service, 2: date, 3: time
  const [selectedDate, setSelectedDate] = useState(null);
  const [confirmSlot, setConfirmSlot] = useState(null);

  useEffect(() => {
    api.get("/services").then(r => setServices(r.data)).catch(() => {});
  }, []);

  // fetch availability for wide window (60 days) each time service changes
  useEffect(() => {
    if (!serviceType) return;
    setLoading(true);
    setSelectedDate(null);
    setStep(2);
    api.get(`/bookings/available-slots?service_type=${serviceType}&days=60`)
      .then(r => setData(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [serviceType]);

  const slotsByDate = useMemo(() => {
    const g = {};
    (data.slots || []).forEach(s => { g[s.date] = g[s.date] || []; g[s.date].push(s); });
    return g;
  }, [data]);

  const selectedService = services.find(s => s.key === serviceType);

  const confirmBooking = async () => {
    if (!confirmSlot) return;
    try {
      const body = confirmSlot.slot_id
        ? { slot_id: confirmSlot.slot_id }
        : { date: confirmSlot.date, time: confirmSlot.time, service_type: serviceType };
      const res = await api.post("/bookings", body);
      const newBookingId = res.data?.id;
      toast.success("Prenotazione confermata!");
      setConfirmSlot(null);
      if (newBookingId && window.confirm("Vuoi ripetere questa prenotazione? Clicca OK per scegliere il numero di settimane.")) {
        const raw = window.prompt("Quante settimane vuoi ripetere (1-52)?", "4");
        const n = parseInt(raw || "0");
        if (n > 0 && n <= 52) {
          try {
            const r = await api.post(`/bookings/${newBookingId}/repeat`, { occurrences: n, frequency: "weekly" });
            const c = (r.data.created || []).length;
            const s = (r.data.skipped || []).length;
            let msg = `Create ${c} prenotazioni ripetute.`;
            if (s > 0) msg += ` Saltate ${s} date: ${r.data.skipped.map(x => `${formatDateIT(x.date)} (${x.reason})`).join(", ")}`;
            toast.success(msg);
          } catch (e) { toast.error(formatApiError(e)); }
        }
      }
      // refresh availability
      const r2 = await api.get(`/bookings/available-slots?service_type=${serviceType}&days=60`);
      setData(r2.data);
      onChanged?.();
      setStep(2); setSelectedDate(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="card-surface p-4 md:p-6">
      {/* Stepper */}
      <div className="flex items-center gap-2 md:gap-4 mb-6 flex-wrap text-xs">
        <StepBullet n={1} label="Servizio" active={step === 1} done={step > 1} onClick={() => setStep(1)} />
        <ChevronRight size={14} className="text-white/30" />
        <StepBullet n={2} label="Data" active={step === 2} done={step > 2} disabled={!serviceType} onClick={() => { if (serviceType) setStep(2); }} />
        <ChevronRight size={14} className="text-white/30" />
        <StepBullet n={3} label="Orario" active={step === 3} disabled={!selectedDate} onClick={() => { if (selectedDate) setStep(3); }} />
      </div>

      {/* STEP 1: Service picker */}
      {step === 1 && (
        <div>
          <div className="label-caps mb-4">Scegli il servizio</div>
          <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
            {services.map(s => (
              <button
                key={s.key}
                data-testid={`step1-service-${s.key}`}
                onClick={() => setServiceType(s.key)}
                className="text-left border border-white/10 hover:border-[#A855F7] p-4 transition-colors"
                style={{ borderLeft: `4px solid ${s.color}` }}
              >
                <div className="font-heading font-black uppercase tracking-tight text-lg">{s.label}</div>
                {s.trainer_name && <div data-testid={`step1-trainer-${s.key}`} className="text-[11px] uppercase tracking-widest text-[#A855F7] mt-0.5">con {s.trainer_name}</div>}
                {s.description && <div className="text-white/60 text-xs mt-1">{s.description}</div>}
                <div className="flex gap-3 mt-3 text-[11px] text-white/50">
                  <span className="flex items-center gap-1"><Clock size={12} /> {s.duration} min</span>
                  <span className="flex items-center gap-1"><Users size={12} /> max {s.capacity}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* STEP 2: Date picker (monthly calendar) */}
      {step === 2 && selectedService && (
        <div>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="label-caps mb-1">Servizio selezionato</div>
              <div className="font-heading font-black uppercase text-lg" style={{ color: selectedService.color }}>{selectedService.label}</div>
            </div>
            <button data-testid="change-service" onClick={() => setStep(1)} className="btn-ghost !py-1.5 !text-[11px]">
              ← Cambia servizio
            </button>
          </div>
          {loading ? (
            <div className="text-white/40 text-sm py-12 text-center">Caricamento disponibilità…</div>
          ) : (
            <MonthCalendar
              cursor={monthCursor}
              onCursorChange={setMonthCursor}
              availableDates={slotsByDate}
              color={selectedService.color}
              onSelect={(iso) => { setSelectedDate(iso); setStep(3); }}
            />
          )}
        </div>
      )}

      {/* STEP 3: Time picker */}
      {step === 3 && selectedDate && selectedService && (
        <div>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="label-caps mb-1">{selectedService.label}</div>
              <div className="font-heading font-black uppercase text-lg">{formatDateITFull(selectedDate)}</div>
            </div>
            <button data-testid="change-date" onClick={() => setStep(2)} className="btn-ghost !py-1.5 !text-[11px]">
              ← Cambia data
            </button>
          </div>

          {(selectedService?.capacity || 1) > 1 && (
            <div className="flex items-start gap-2 text-xs text-white/60 border border-white/10 p-3 mb-4">
              <Info size={14} className="mt-0.5 text-[#A855F7] shrink-0" />
              <span>{selectedService.label} è un servizio di gruppo: vedi solo gli orari programmati dalla trainer con posti ancora liberi.</span>
            </div>
          )}

          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {(slotsByDate[selectedDate] || []).map((s, i) => (
              <button
                key={(s.slot_id || "") + s.time + i}
                data-testid={`avail-${selectedDate}-${s.time}`}
                onClick={() => setConfirmSlot(s)}
                className="border border-white/10 hover:border-[#A855F7] hover:bg-[#A855F7]/10 p-3 text-sm transition-colors text-left"
                style={{ borderLeft: `3px solid ${selectedService.color}` }}
              >
                <div className="font-heading font-bold">{s.time}</div>
                {(selectedService?.capacity || 1) > 1 && (
                  <div className="text-[10px] text-white/50 mt-0.5">
                    {s.capacity_remaining} post{s.capacity_remaining === 1 ? "o" : "i"}
                  </div>
                )}
              </button>
            ))}
            {(slotsByDate[selectedDate] || []).length === 0 && (
              <div className="col-span-full text-white/40 text-sm py-8 text-center">Nessun orario disponibile in questa data.</div>
            )}
          </div>
        </div>
      )}

      {confirmSlot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setConfirmSlot(null)} data-testid="confirm-booking-backdrop">
          <div className="bg-[#120B19] border border-white/10 max-w-sm w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="label-caps mb-2">Conferma prenotazione</div>
            <div className="font-heading font-black text-2xl uppercase tracking-tight mb-1" style={{ color: selectedService?.color || "#A855F7" }}>{selectedService?.label}</div>
            <div className="text-white/70 mb-4">{formatDateITFull(confirmSlot.date)} · {confirmSlot.time} · {confirmSlot.duration_minutes} min</div>
            <div className="text-xs text-white/50 mb-6">1 lezione verrà scalata dal tuo pacchetto al termine della conferma.</div>
            <div className="flex gap-2">
              <button onClick={() => setConfirmSlot(null)} className="flex-1 border border-white/15 p-3 text-sm hover:border-white/40" data-testid="cancel-confirm-booking">Annulla</button>
              <button onClick={confirmBooking} data-testid="confirm-booking-btn" className="flex-1 p-3 text-sm font-heading uppercase tracking-widest text-black flex items-center justify-center gap-2" style={{ backgroundColor: selectedService?.color || "#A855F7" }}>
                Conferma <Check size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StepBullet({ n, label, active, done, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className={`flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed`}
      data-testid={`step-${n}`}>
      <span className={`w-6 h-6 flex items-center justify-center rounded-full text-[10px] font-bold ${active ? "bg-[#A855F7] text-white" : done ? "bg-white/10 text-white" : "bg-white/5 text-white/40"}`}>
        {done ? "✓" : n}
      </span>
      <span className={`font-heading uppercase tracking-widest ${active ? "text-white" : "text-white/40"}`}>{label}</span>
    </button>
  );
}

function MonthCalendar({ cursor, onCursorChange, availableDates, color, onSelect }) {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1);
  const weekdayStart = (firstDay.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = toISODate(new Date());

  const nav = (dir) => {
    const d = new Date(cursor);
    d.setMonth(d.getMonth() + dir);
    onCursorChange(d);
  };

  const cells = [];
  for (let i = 0; i < weekdayStart; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) {
    const iso = toISODate(new Date(year, month, day));
    cells.push({ day, iso, hasSlots: (availableDates[iso] || []).length > 0, isPast: iso < today });
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => nav(-1)} data-testid="cal-prev" className="p-2 hover:bg-white/5 rounded"><ChevronLeft size={16} /></button>
        <div className="font-heading font-bold uppercase tracking-wider text-lg">{MONTHS[month]} {year}</div>
        <button onClick={() => nav(1)} data-testid="cal-next" className="p-2 hover:bg-white/5 rounded"><ChevronRight size={16} /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center mb-1">
        {["Lun","Mar","Mer","Gio","Ven","Sab","Dom"].map(d => (
          <div key={d} className="text-[10px] uppercase tracking-widest text-white/40 py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          if (!c) return <div key={i} />;
          // Hide (grey out) days without any availability (blocked or fully booked)
          const disabled = c.isPast || !c.hasSlots;
          return (
            <button
              key={i}
              disabled={disabled}
              onClick={() => onSelect(c.iso)}
              data-testid={`cal-day-${c.iso}`}
              className={`aspect-square flex flex-col items-center justify-center border text-sm transition-colors ${
                disabled
                  ? "border-white/5 text-white/20 cursor-not-allowed"
                  : "border-white/10 hover:bg-[#A855F7]/10 hover:border-[#A855F7] text-white"
              }`}
              style={c.hasSlots && !c.isPast ? { borderLeft: `3px solid ${color}` } : {}}
            >
              <span className="font-heading font-bold">{c.day}</span>
              {c.hasSlots && !c.isPast && (
                <span className="text-[9px] uppercase tracking-widest text-white/50 mt-0.5">
                  {(availableDates[c.iso] || []).length} orari
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
